/* ============================================
   Conecta Agenda — gestión nativa dentro del portal
   Habla con la API de organizador de Agenda a través del BFF del ERP
   (/api/portal/apps/agenda/api/*), que inyecta la sesión server-side.
   El cliente nunca sale del portal ni vuelve a loguearse.
   ============================================ */

import { useState, useEffect, useCallback } from 'react'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import { copiarTexto } from '@/lib/clipboard'
import { descargarCsv, type CsvColumn } from '@/lib/csv'

const BASE = '/api/portal/apps/agenda/api'

export type AgendaTab = 'tipos' | 'disponibilidad' | 'reservas' | 'link' | 'perfil' | 'apikeys'

/* ─── Tipos de la API de Agenda ─── */
interface TipoReunion {
  id: string
  slug: string
  nombre: string
  descripcion: string | null
  duracionMin: number
  bufferAntesMin: number
  bufferDespuesMin: number
  anticipacionMinHoras: number
  horizonteMaxDias: number
  granularidadMin: number
  ubicacion: 'google_meet' | 'presencial' | 'telefono'
  activo: boolean
}

interface Regla { diaSemana: number; horaInicio: string; horaFin: string }

interface Perfil {
  slug: string
  nombre: string
  email: string
  fotoUrl: string | null
  bio: string | null
  zonaHoraria: string
  /**
   * Descanso mínimo entre dos reuniones cualesquiera, sea cual sea el tipo (min).
   * Opcional: falta si Agenda es anterior al 2026-09-23; ahí el control no se pinta.
   */
  descansoMin?: number
}

interface ApiKey {
  id: string
  nombre: string
  prefijo: string
  ultimoUso: string | null
  revocada: boolean
  creadaEn: string
}

type ClasePregunta = 'texto' | 'texto_largo' | 'telefono' | 'opcion_unica' | 'opcion_multiple'
interface Pregunta {
  etiqueta: string
  ayuda: string | null
  clase: ClasePregunta
  opciones: string[]
  obligatoria: boolean
}

const CLASE_LABEL: Record<ClasePregunta, string> = {
  texto: 'Texto corto',
  texto_largo: 'Texto largo',
  telefono: 'Teléfono',
  opcion_unica: 'Una opción',
  opcion_multiple: 'Varias opciones',
}

interface RespuestaReserva { preguntaId: string; etiqueta: string; valor: string }

interface Reserva {
  id: string
  invitadoNombre: string
  invitadoEmail: string
  inicioUtc: string
  finUtc: string
  estado: 'confirmada' | 'reagendada' | 'cancelada'
  origen: string | null
  tipoReunionId: string
  /** Cuándo se agendó, que no es cuándo ocurre: mide la anticipación con que reservan. */
  creadaEn?: string | null
  meetUrl?: string | null
  referenciaExterna?: string | null
  motivoCancelacion?: string | null
  notas?: string | null
  respuestas?: RespuestaReserva[]
}

const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
/** Opciones del descanso entre reuniones; un valor guardado fuera de la lista se añade al vuelo. */
const DESCANSOS = [0, 5, 10, 15, 20, 30, 45, 60, 90, 120]
const UBICACION_LABEL: Record<TipoReunion['ubicacion'], string> = {
  google_meet: 'Google Meet',
  presencial: 'Presencial',
  telefono: 'Teléfono',
}
const ESTADO_RESERVA: Record<Reserva['estado'], string> = {
  confirmada: 'badge-activo',
  reagendada: 'badge-pausado',
  cancelada: 'badge-cancelado',
}

function fmtFechaHora(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-PE', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  } catch { return iso }
}

/**
 * Fecha para el CSV: `dd/mm/aaaa hh:mm` armado a mano, no `toLocaleString`.
 * El formato largo de es-PE mete una coma entre fecha y hora ("15/09/2026, 17:30")
 * y así Excel no lo reconoce como fecha, lo deja como texto y no se puede ordenar
 * ni restar. Mismo huso que la tabla (el del navegador de quien exporta).
 */
function fmtCsvFechaHora(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
}

/**
 * Snippet de embed. El formato lo define `embed.js` de Agenda: espera
 * `data-slug` (el organizador) y opcionalmente `data-tipo` (el tipo de reunión).
 * Sin `data-tipo` incrusta la página completa con todos los tipos.
 */
function codigoEmbed(orgSlug: string | null, tipoSlug?: string): string {
  const slug = orgSlug || 'tu-slug'
  const tipo = tipoSlug ? `\n        data-tipo="${tipoSlug}"` : ''
  return `<script src="https://agenda.1conecta.com/embed.js"\n        data-slug="${slug}"${tipo}\n        data-alto-min="660"></script>`
}

function slugify(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40)
}

/* ═══════════════ Banner: conexión con Google Calendar ═══════════════ */
function GoogleBanner() {
  const api = useApi()
  const [estado, setEstado] = useState<{ conectado?: boolean; email?: string } | null>(null)
  const [yendo, setYendo] = useState(false)

  useEffect(() => {
    api.get<{ conectado?: boolean; email?: string }>(`${BASE}/oauth/google/estado`)
      .then(setEstado).catch(() => setEstado(null))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function conectar() {
    setYendo(true)
    const w = window.open('', '_blank')
    try {
      const r = await api.get<{ url?: string }>(`${BASE}/oauth/google/inicio`)
      if (r?.url && w) w.location.href = r.url
      else if (r?.url) window.location.href = r.url
      else { w?.close(); showToast('No se pudo iniciar la conexión con Google', 'error') }
    } catch {
      w?.close()
      showToast('No se pudo iniciar la conexión con Google', 'error')
    } finally { setYendo(false) }
  }

  if (estado?.conectado) {
    return (
      <div className="ag-banner ag-banner--ok">
        <span>Google Calendar conectado{estado.email ? ` · ${estado.email}` : ''}. Tu disponibilidad ya considera tus eventos.</span>
      </div>
    )
  }
  return (
    <div className="ag-banner">
      <span>Conectá tu Google Calendar para que solo se ofrezcan horarios en los que estás libre y se cree el evento con Meet.</span>
      <button className="btn btn-sm btn-primary" onClick={conectar} disabled={yendo}>
        {yendo ? 'Abriendo…' : 'Conectar Google'}
      </button>
    </div>
  )
}

/* ═══════════════ Tipos de reunión ═══════════════ */
const TIPO_VACIO: Partial<TipoReunion> = {
  nombre: '', slug: '', descripcion: '', duracionMin: 30,
  bufferAntesMin: 0, bufferDespuesMin: 0, anticipacionMinHoras: 4,
  horizonteMaxDias: 60, granularidadMin: 30, ubicacion: 'google_meet', activo: true,
}

function TiposReunion({ publicUrl, orgSlug }: { publicUrl: string | null; orgSlug: string | null }) {
  const api = useApi()
  const [tipos, setTipos] = useState<TipoReunion[]>([])
  const [loading, setLoading] = useState(true)
  const [editando, setEditando] = useState<Partial<TipoReunion> | null>(null)
  const [preguntasDe, setPreguntasDe] = useState<TipoReunion | null>(null)
  const [guardando, setGuardando] = useState(false)
  const [menuAbierto, setMenuAbierto] = useState<string | null>(null)

  // Cerrar el menú al hacer clic afuera o con Escape.
  useEffect(() => {
    if (!menuAbierto) return
    const fuera = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.ag-menu')) setMenuAbierto(null)
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuAbierto(null) }
    document.addEventListener('mousedown', fuera)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', fuera)
      document.removeEventListener('keydown', esc)
    }
  }, [menuAbierto])

  const cargar = useCallback(() => {
    setLoading(true)
    api.get<TipoReunion[]>(`${BASE}/tipos-reunion`)
      .then(d => setTipos(Array.isArray(d) ? d : []))
      .catch(() => setTipos([]))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { cargar() }, [cargar])

  async function guardar(e: React.FormEvent) {
    e.preventDefault()
    if (!editando?.nombre?.trim()) { showToast('Poné un nombre', 'error'); return }
    const cuerpo = {
      ...TIPO_VACIO, ...editando,
      slug: (editando.slug || slugify(editando.nombre)).trim(),
      descripcion: editando.descripcion || null,
    }
    setGuardando(true)
    try {
      if (editando.id) await api.put(`${BASE}/tipos-reunion/${editando.id}`, cuerpo)
      else await api.post(`${BASE}/tipos-reunion`, cuerpo)
      showToast('Tipo de reunión guardado', 'success')
      setEditando(null)
      cargar()
    } catch {
      showToast('No se pudo guardar. Revisá que el enlace no esté repetido.', 'error')
    } finally { setGuardando(false) }
  }

  function copiarEnlace(url: string) {
    copiarTexto(url).then(ok =>
      showToast(ok ? 'Enlace copiado' : 'No se pudo copiar', ok ? 'success' : 'error'))
  }

  function copiarEmbed(tipoSlug: string) {
    copiarTexto(codigoEmbed(orgSlug, tipoSlug)).then(ok =>
      showToast(ok ? 'Código de embed copiado' : 'No se pudo copiar', ok ? 'success' : 'error'))
  }

  async function eliminar(t: TipoReunion) {
    if (!confirm(`¿Eliminar "${t.nombre}"?`)) return
    try {
      await api.del(`${BASE}/tipos-reunion/${t.id}`)
      showToast('Tipo eliminado', 'success')
      cargar()
    } catch { showToast('No se pudo eliminar', 'error') }
  }

  if (loading) return <div className="pad-block"><p className="pad-prose">Cargando tipos de reunión…</p></div>

  return (
    <>
      <div className="ag-toolbar">
        <span className="pad-note" style={{ margin: 0 }}>
          {tipos.length === 0 ? 'Todavía no creaste ningún tipo de reunión.' : `${tipos.length} tipo${tipos.length === 1 ? '' : 's'} de reunión`}
        </span>
        <button className="btn btn-sm btn-primary" onClick={() => setEditando({ ...TIPO_VACIO })}>Nuevo tipo</button>
      </div>

      {tipos.length > 0 && (
        <div className="ag-list">
          {tipos.map(t => {
            const enlace = `${(publicUrl || 'https://agenda.1conecta.com').replace(/\/$/, '')}/${t.slug}`
            return (
              <div key={t.id} className={`ag-row ${t.activo ? '' : 'ag-row--pausado'}`}>
                <div className="ag-row-main">
                  <div className="ag-row-title">
                    {t.nombre}
                    {!t.activo && <span className="badge badge-pausado">Pausado</span>}
                  </div>
                  <div className="ag-row-meta">
                    <span><strong>{t.duracionMin}</strong> min</span>
                    <span className="ag-sep">·</span>
                    <span>{UBICACION_LABEL[t.ubicacion]}</span>
                    {/* El separador viaja DENTRO del slug: al ocultarlo en móvil
                        no queda un "·" colgando al final. */}
                    <span className="ag-row-slug"><span className="ag-sep">·</span>/{t.slug}</span>
                  </div>
                </div>

                <div className="ag-row-actions">
                  {/* Acción principal: es lo que el organizador hace 10 veces al día */}
                  <button className="btn btn-primary ag-btn-copiar" onClick={() => copiarEnlace(enlace)}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                    Copiar enlace
                  </button>

                  {/* El resto vive en el menú, para que solo destaque lo importante */}
                  <div className="ag-menu">
                    <button
                      className="ag-menu-btn"
                      aria-haspopup="menu"
                      aria-expanded={menuAbierto === t.id}
                      aria-label={`Más opciones de ${t.nombre}`}
                      onClick={() => setMenuAbierto(m => (m === t.id ? null : t.id))}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" />
                      </svg>
                    </button>

                    {menuAbierto === t.id && (
                      <div className="ag-menu-pop" role="menu">
                        <a href={enlace} target="_blank" rel="noreferrer" role="menuitem" onClick={() => setMenuAbierto(null)}>
                          Ver página de reservas
                        </a>
                        <button role="menuitem" onClick={() => { setMenuAbierto(null); copiarEmbed(t.slug) }}>
                          Copiar código de embed
                        </button>
                        <button role="menuitem" onClick={() => { setMenuAbierto(null); setEditando(t) }}>Editar</button>
                        <button role="menuitem" onClick={() => { setMenuAbierto(null); setPreguntasDe(t) }}>Preguntas</button>
                        <button role="menuitem" className="ag-danger" onClick={() => { setMenuAbierto(null); eliminar(t) }}>Eliminar</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {editando && (
        <div className="pa-modal-overlay" onClick={() => setEditando(null)}>
          <form className="pa-modal" onClick={e => e.stopPropagation()} onSubmit={guardar}>
            <button type="button" className="pa-modal-close" onClick={() => setEditando(null)} aria-label="Cerrar">✕</button>
            <div className="pa-modal-header">
              <h3>{editando.id ? 'Editar tipo de reunión' : 'Nuevo tipo de reunión'}</h3>
            </div>

            <div className="form-group">
              <label className="form-label">Nombre</label>
              <input className="form-input" value={editando.nombre ?? ''} autoFocus
                onChange={e => setEditando({ ...editando, nombre: e.target.value, slug: editando.id ? editando.slug : slugify(e.target.value) })} />
            </div>
            <div className="form-group">
              <label className="form-label">Enlace (slug)</label>
              <input className="form-input" value={editando.slug ?? ''}
                onChange={e => setEditando({ ...editando, slug: slugify(e.target.value) })} />
            </div>
            <div className="form-group">
              <label className="form-label">Descripción</label>
              <textarea className="form-input" rows={2} value={editando.descripcion ?? ''}
                onChange={e => setEditando({ ...editando, descripcion: e.target.value })} />
            </div>

            <div className="ag-form-row">
              <div className="form-group">
                <label className="form-label">Duración (min)</label>
                <input className="form-input" type="number" min={5} max={480} value={editando.duracionMin ?? 30}
                  onChange={e => setEditando({ ...editando, duracionMin: Number(e.target.value) })} />
              </div>
              <div className="form-group">
                <label className="form-label">Intervalo (min)</label>
                <input className="form-input" type="number" min={5} max={120} value={editando.granularidadMin ?? 30}
                  onChange={e => setEditando({ ...editando, granularidadMin: Number(e.target.value) })} />
              </div>
            </div>

            <div className="ag-form-row">
              <div className="form-group">
                <label className="form-label">Descanso antes (min)</label>
                <input className="form-input" type="number" min={0} max={240} value={editando.bufferAntesMin ?? 0}
                  onChange={e => setEditando({ ...editando, bufferAntesMin: Number(e.target.value) })} />
              </div>
              <div className="form-group">
                <label className="form-label">Descanso después (min)</label>
                <input className="form-input" type="number" min={0} max={240} value={editando.bufferDespuesMin ?? 0}
                  onChange={e => setEditando({ ...editando, bufferDespuesMin: Number(e.target.value) })} />
              </div>
            </div>
            {/* Estos parecen la regla general y no lo son: solo miran al reservar ESTE tipo.
                La regla entre todas las reuniones es `descansoMin`, en Disponibilidad. */}
            <p className="form-hint" style={{ margin: '0 0 16px' }}>
              Solo aplican al reservar este tipo. Para un descanso entre todas tus reuniones, usá
              «Descanso entre reuniones» en Disponibilidad; si es mayor, manda ese.
            </p>

            <div className="ag-form-row">
              <div className="form-group">
                <label className="form-label">Anticipación mínima (h)</label>
                <input className="form-input" type="number" min={0} max={720} value={editando.anticipacionMinHoras ?? 4}
                  onChange={e => setEditando({ ...editando, anticipacionMinHoras: Number(e.target.value) })} />
              </div>
              <div className="form-group">
                <label className="form-label">Se puede agendar hasta (días)</label>
                <input className="form-input" type="number" min={1} max={365} value={editando.horizonteMaxDias ?? 60}
                  onChange={e => setEditando({ ...editando, horizonteMaxDias: Number(e.target.value) })} />
              </div>
            </div>

            <div className="ag-form-row">
              <div className="form-group">
                <label className="form-label">Modalidad</label>
                <select className="form-input" value={editando.ubicacion ?? 'google_meet'}
                  onChange={e => setEditando({ ...editando, ubicacion: e.target.value as TipoReunion['ubicacion'] })}>
                  <option value="google_meet">Google Meet</option>
                  <option value="presencial">Presencial</option>
                  <option value="telefono">Teléfono</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Estado</label>
                <select className="form-input" value={editando.activo === false ? '0' : '1'}
                  onChange={e => setEditando({ ...editando, activo: e.target.value === '1' })}>
                  <option value="1">Activo</option>
                  <option value="0">Pausado</option>
                </select>
              </div>
            </div>

            <button type="submit" className="btn btn-primary pad-cta" disabled={guardando}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </form>
        </div>
      )}

      {preguntasDe && <PreguntasModal tipo={preguntasDe} onClose={() => setPreguntasDe(null)} />}
    </>
  )
}

/* ═══════════════ Disponibilidad ═══════════════ */
function Disponibilidad() {
  const api = useApi()
  const [reglas, setReglas] = useState<Regla[]>([])
  const [loading, setLoading] = useState(true)
  const [guardando, setGuardando] = useState(false)
  // El descanso vive en el perfil de Agenda (vale para todos los tipos), pero se edita
  // acá porque decide cuándo te pueden reservar, igual que los horarios.
  const [descanso, setDescanso] = useState(0)
  const [descansoGuardado, setDescansoGuardado] = useState(0)
  const [soportaDescanso, setSoportaDescanso] = useState(false)

  useEffect(() => {
    api.get<Regla[]>(`${BASE}/disponibilidad`)
      .then(d => setReglas(Array.isArray(d) ? d.map(r => ({ ...r, horaInicio: r.horaInicio.slice(0, 5), horaFin: r.horaFin.slice(0, 5) })) : []))
      .catch(() => setReglas([]))
      .finally(() => setLoading(false))
    // Solo se pinta si Agenda devuelve el campo: un Agenda sin él rechaza el PUT.
    api.get<Perfil>(`${BASE}/perfil`)
      .then(p => {
        if (typeof p?.descansoMin !== 'number') return
        setDescanso(p.descansoMin)
        setDescansoGuardado(p.descansoMin)
        setSoportaDescanso(true)
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function agregar(dia: number) {
    setReglas(r => [...r, { diaSemana: dia, horaInicio: '09:00', horaFin: '17:00' }])
  }
  function quitar(idx: number) {
    setReglas(r => r.filter((_, i) => i !== idx))
  }
  function editar(idx: number, campo: 'horaInicio' | 'horaFin', valor: string) {
    setReglas(r => r.map((x, i) => (i === idx ? { ...x, [campo]: valor } : x)))
  }

  async function guardar() {
    setGuardando(true)
    try {
      await api.put(`${BASE}/disponibilidad`, { reglas })
      // Solo el campo del descanso: `PUT /perfil` es parcial y así no pisa lo que se
      // edite en la pestaña Perfil.
      if (soportaDescanso && descanso !== descansoGuardado) {
        await api.put(`${BASE}/perfil`, { descansoMin: descanso })
        setDescansoGuardado(descanso)
      }
      showToast('Disponibilidad guardada', 'success')
    } catch {
      showToast('No se pudo guardar la disponibilidad', 'error')
    } finally { setGuardando(false) }
  }

  if (loading) return <div className="pad-block"><p className="pad-prose">Cargando disponibilidad…</p></div>

  return (
    <>
      <div className="ag-toolbar">
        <span className="pad-note" style={{ margin: 0 }}>Horarios en los que aceptás reuniones (tu zona horaria).</span>
        <button className="btn btn-sm btn-primary" onClick={guardar} disabled={guardando}>
          {guardando ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>

      <div className="pad-block">
        {DIAS.map((nombre, dia) => {
          const delDia = reglas.map((r, i) => ({ r, i })).filter(x => x.r.diaSemana === dia)
          return (
            <div key={dia} className="ag-dia">
              <span className="ag-dia-nombre">{nombre}</span>
              <div className="ag-dia-rangos">
                {delDia.length === 0 && <span className="pad-note" style={{ margin: 0 }}>Sin horarios</span>}
                {delDia.map(({ r, i }) => (
                  <div key={i} className="ag-rango">
                    <input className="form-input" type="time" value={r.horaInicio} onChange={e => editar(i, 'horaInicio', e.target.value)} />
                    <span>a</span>
                    <input className="form-input" type="time" value={r.horaFin} onChange={e => editar(i, 'horaFin', e.target.value)} />
                    <button className="btn btn-sm btn-ghost" onClick={() => quitar(i)} title="Quitar">✕</button>
                  </div>
                ))}
              </div>
              <button className="btn btn-sm btn-secondary" onClick={() => agregar(dia)}>+ Agregar</button>
            </div>
          )
        })}
      </div>

      {soportaDescanso && (
        <div className="pad-block">
          <div className="form-group" style={{ maxWidth: 280, marginBottom: 0 }}>
            <label className="form-label" htmlFor="ag-descanso">Descanso entre reuniones</label>
            <select id="ag-descanso" className="form-input" value={descanso}
              onChange={e => setDescanso(Number(e.target.value))}>
              {[...new Set([...DESCANSOS, descanso])].sort((a, b) => a - b).map(m => (
                <option key={m} value={m}>{m === 0 ? 'Sin descanso' : `${m} minutos`}</option>
              ))}
            </select>
          </div>
          <p className="pad-note">
            Tiempo libre entre dos reuniones, sean del tipo que sean, para que una que se alarga
            no se coma la siguiente. Con 30 minutos, si te reservan de 10:00 a 10:30, el siguiente
            horario que se ofrece es a las 11:00. También cuenta frente a los demás eventos de tu
            Google Calendar. Se guarda con «Guardar cambios».
          </p>
        </div>
      )}
    </>
  )
}

/* ═══════════════ Reservas ═══════════════ */

/**
 * Ventana de la consulta. La API filtra por fecha **de la reunión** y sin `desde`
 * mira solo una semana atrás, así que el organizador veía un recorte de su agenda
 * sin saberlo; acá se elige explícitamente. Las reuniones futuras entran siempre
 * (el filtro no tiene tope superior).
 */
const RANGOS_RESERVA = [
  { clave: '90', etiqueta: 'Últimos 90 días', dias: 90 },
  { clave: '30', etiqueta: 'Últimos 30 días', dias: 30 },
  { clave: '365', etiqueta: 'Último año', dias: 365 },
  { clave: 'todo', etiqueta: 'Todo el historial', dias: null },
] as const

type ClaveRango = typeof RANGOS_RESERVA[number]['clave']

const ORIGEN_LABEL: Record<string, string> = {
  publica: 'Página pública',
  embed: 'Embed en tu web',
  api: 'API',
}

function Reservas() {
  const api = useApi()
  const [reservas, setReservas] = useState<Reserva[]>([])
  const [tipos, setTipos] = useState<Record<string, string>>({})
  const [rango, setRango] = useState<ClaveRango>('90')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    const dias = RANGOS_RESERVA.find(r => r.clave === rango)?.dias ?? null
    const desde = dias ? new Date(Date.now() - dias * 86_400_000) : new Date(0)
    const qs = `?desde=${encodeURIComponent(desde.toISOString())}&limite=2000`
    Promise.all([
      api.get<Reserva[]>(`${BASE}/reservas/mias${qs}`).catch(() => []),
      api.get<TipoReunion[]>(`${BASE}/tipos-reunion`).catch(() => []),
    ]).then(([r, t]) => {
      setReservas(Array.isArray(r) ? r : [])
      const map: Record<string, string> = {}
      ;(Array.isArray(t) ? t : []).forEach(x => { map[x.id] = x.nombre })
      setTipos(map)
    }).finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rango])

  /**
   * Descarga lo que está en pantalla, con todo lo que la reserva guarda. Las
   * respuestas del formulario abren **una columna por pregunta** (la unión de las
   * etiquetas que aparezcan): si el organizador cambió las preguntas con el tiempo,
   * cada reserva llena solo las suyas y las demás quedan vacías, que es lo honesto.
   */
  function descargar() {
    if (reservas.length === 0) return
    const etiquetas: string[] = []
    reservas.forEach(r => (r.respuestas ?? []).forEach(x => {
      if (x.etiqueta && !etiquetas.includes(x.etiqueta)) etiquetas.push(x.etiqueta)
    }))

    const columnas: CsvColumn<Reserva>[] = [
      ['Creada', r => fmtCsvFechaHora(r.creadaEn)],
      ['Inicio de la reunión', r => fmtCsvFechaHora(r.inicioUtc)],
      ['Fin de la reunión', r => fmtCsvFechaHora(r.finUtc)],
      ['Invitado', r => r.invitadoNombre],
      ['Email', r => r.invitadoEmail],
      ['Tipo de reunión', r => tipos[r.tipoReunionId] ?? ''],
      ['Estado', r => r.estado],
      ['Origen', r => (r.origen ? ORIGEN_LABEL[r.origen] ?? r.origen : '')],
      ['Motivo de cancelación', r => r.motivoCancelacion ?? ''],
      ['Enlace Meet', r => r.meetUrl ?? ''],
      ['Referencia externa', r => r.referenciaExterna ?? ''],
      ['Notas', r => r.notas ?? ''],
      ...etiquetas.map((et): CsvColumn<Reserva> => [
        et,
        r => (r.respuestas ?? []).filter(x => x.etiqueta === et).map(x => x.valor).join(' | '),
      ]),
    ]

    const hoy = new Date().toISOString().slice(0, 10)
    descargarCsv(`reservas-${hoy}.csv`, columnas, reservas)
  }

  const selector = (
    <select className="form-input" value={rango} onChange={e => setRango(e.target.value as ClaveRango)}>
      {RANGOS_RESERVA.map(r => <option key={r.clave} value={r.clave}>{r.etiqueta}</option>)}
    </select>
  )

  return (
    <div className="pad-block">
      <div className="ag-toolbar">
        <div className="ag-rango">
          {selector}
          <span className="pad-note" style={{ margin: 0 }}>
            {loading ? 'Cargando…' : `${reservas.length} reserva${reservas.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <button className="btn btn-sm btn-secondary" onClick={descargar} disabled={loading || reservas.length === 0}>
          Descargar Excel
        </button>
      </div>

      {loading ? <p className="pad-prose">Cargando reservas…</p>
        : reservas.length === 0 ? (
          <p className="pad-prose">
            {rango === 'todo'
              ? 'Todavía no tenés reservas. Compartí tu enlace para empezar a recibirlas.'
              : 'No hay reservas en este período. Probá con un rango más amplio.'}
          </p>
        ) : (
          <div className="pad-tarifas-wrap">
            <table className="pad-tarifas">
              <thead>
                <tr><th>Cuándo</th><th>Se agendó</th><th>Invitado</th><th>Tipo</th><th>Estado</th></tr>
              </thead>
              <tbody>
                {reservas.map(r => (
                  <tr key={r.id}>
                    <td>{fmtFechaHora(r.inicioUtc)}</td>
                    <td>{r.creadaEn ? fmtFechaHora(r.creadaEn) : '—'}</td>
                    <td>
                      <strong>{r.invitadoNombre}</strong>
                      <div className="pad-note" style={{ margin: 0 }}>{r.invitadoEmail}</div>
                    </td>
                    <td>{tipos[r.tipoReunionId] ?? '—'}</td>
                    <td><span className={`badge ${ESTADO_RESERVA[r.estado]}`}>{r.estado}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  )
}

/* ═══════════════ Mi link / embed ═══════════════ */
function MiLink({ publicUrl, orgSlug }: { publicUrl: string | null; orgSlug: string | null }) {
  const url = publicUrl || 'https://agenda.1conecta.com'
  const embed = codigoEmbed(orgSlug)

  function copiar(texto: string, que: string) {
    copiarTexto(texto).then(ok =>
      showToast(ok ? `${que} copiado` : 'No se pudo copiar', ok ? 'success' : 'error'))
  }

  return (
    <>
      <div className="pad-block">
        <h2 className="pad-block-title">Tu página de reservas</h2>
        <p className="pad-prose">Compartí este enlace para que te agenden. Muestra tus tipos de reunión activos y tu disponibilidad real.</p>
        <div className="ag-copy">
          <code>{url}</code>
          <button className="btn btn-sm btn-secondary" onClick={() => copiar(url, 'Enlace')}>Copiar</button>
          <a className="btn btn-sm btn-primary" href={url} target="_blank" rel="noreferrer">Abrir</a>
        </div>
      </div>

      <div className="pad-block">
        <h2 className="pad-block-title">Incrustar en tu web</h2>
        <p className="pad-prose">Pegá este código en tu sitio para mostrar el calendario embebido.</p>
        <div className="ag-copy">
          <code className="ag-copy-code">{embed}</code>
          <button className="btn btn-sm btn-secondary" onClick={() => copiar(embed, 'Código')}>Copiar</button>
        </div>
      </div>
    </>
  )
}

/* ═══════════════ Perfil del organizador ═══════════════ */
function PerfilAgenda() {
  const api = useApi()
  const [perfil, setPerfil] = useState<Perfil | null>(null)
  const [loading, setLoading] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [subiendo, setSubiendo] = useState(false)

  const cargar = useCallback(() => {
    api.get<Perfil>(`${BASE}/perfil`)
      .then(setPerfil).catch(() => setPerfil(null))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => { cargar() }, [cargar])

  async function guardar(e: React.FormEvent) {
    e.preventDefault()
    if (!perfil) return
    setGuardando(true)
    try {
      await api.put(`${BASE}/perfil`, {
        nombre: perfil.nombre,
        bio: perfil.bio || null,
        zonaHoraria: perfil.zonaHoraria,
      })
      showToast('Perfil guardado', 'success')
    } catch {
      showToast('No se pudo guardar el perfil', 'error')
    } finally { setGuardando(false) }
  }

  async function subirFoto(file: File) {
    if (file.size > 2 * 1024 * 1024) { showToast('La imagen supera los 2 MB', 'error'); return }
    setSubiendo(true)
    try {
      const fd = new FormData()
      fd.append('archivo', file)
      await api.upload(`${BASE}/perfil/foto`, fd)
      showToast('Foto actualizada', 'success')
      cargar()
    } catch {
      showToast('No se pudo subir la foto', 'error')
    } finally { setSubiendo(false) }
  }

  async function quitarFoto() {
    try {
      await api.del(`${BASE}/perfil/foto`)
      showToast('Foto eliminada', 'success')
      cargar()
    } catch { showToast('No se pudo eliminar la foto', 'error') }
  }

  if (loading) return <div className="pad-block"><p className="pad-prose">Cargando perfil…</p></div>
  if (!perfil) return <div className="pad-block"><p className="pad-prose">No pudimos cargar tu perfil de Agenda.</p></div>

  return (
    <form className="pad-block" onSubmit={guardar}>
      <h2 className="pad-block-title">Tu perfil público</h2>
      <p className="pad-prose">Es lo que ve el invitado en tu página de reservas.</p>

      <div className="ag-perfil-foto">
        {perfil.fotoUrl
          ? <img className="ag-foto" src={perfil.fotoUrl} alt={perfil.nombre} />
          : <span className="ag-foto ag-foto--txt">{(perfil.nombre || '?').charAt(0).toUpperCase()}</span>}
        <div className="ag-perfil-foto-acciones">
          <label className="btn btn-sm btn-secondary">
            {subiendo ? 'Subiendo…' : 'Cambiar foto'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={e => { const f = e.target.files?.[0]; if (f) subirFoto(f); e.target.value = '' }}
            />
          </label>
          {perfil.fotoUrl && <button type="button" className="btn btn-sm btn-ghost" onClick={quitarFoto}>Quitar</button>}
          <span className="pad-note" style={{ margin: 0 }}>JPG, PNG o WebP · máx. 2 MB</span>
        </div>
      </div>

      <div className="ag-form-row">
        <div className="form-group">
          <label className="form-label">Nombre</label>
          <input className="form-input" value={perfil.nombre}
            onChange={e => setPerfil({ ...perfil, nombre: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-label">Zona horaria</label>
          <input className="form-input" value={perfil.zonaHoraria} placeholder="America/Lima"
            onChange={e => setPerfil({ ...perfil, zonaHoraria: e.target.value })} />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Bio</label>
        <textarea className="form-input" rows={3} value={perfil.bio ?? ''}
          onChange={e => setPerfil({ ...perfil, bio: e.target.value })} />
      </div>

      <div className="ag-form-row">
        <div className="form-group">
          <label className="form-label">Email de la cuenta</label>
          <input className="form-input" value={perfil.email} readOnly />
        </div>
        <div className="form-group">
          <label className="form-label">Tu enlace</label>
          <input className="form-input" value={`agenda.1conecta.com/${perfil.slug}`} readOnly />
        </div>
      </div>

      <button type="submit" className="btn btn-primary pad-cta" disabled={guardando}>
        {guardando ? 'Guardando…' : 'Guardar perfil'}
      </button>
    </form>
  )
}

/* ═══════════════ API keys ═══════════════ */
function ApiKeys() {
  const api = useApi()
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(true)
  const [nombre, setNombre] = useState('')
  const [creando, setCreando] = useState(false)
  const [nueva, setNueva] = useState<string | null>(null)

  const cargar = useCallback(() => {
    api.get<ApiKey[]>(`${BASE}/api-keys`)
      .then(d => setKeys(Array.isArray(d) ? d : []))
      .catch(() => setKeys([]))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => { cargar() }, [cargar])

  async function crear(e: React.FormEvent) {
    e.preventDefault()
    if (!nombre.trim()) { showToast('Poné un nombre', 'error'); return }
    setCreando(true)
    try {
      const r = await api.post<{ clave?: string }>(`${BASE}/api-keys`, { nombre: nombre.trim() })
      setNueva(r?.clave ?? null)
      setNombre('')
      cargar()
    } catch {
      showToast('No se pudo crear la clave', 'error')
    } finally { setCreando(false) }
  }

  async function revocar(k: ApiKey) {
    if (!confirm(`¿Revocar "${k.nombre}"? Las integraciones que la usen dejarán de funcionar.`)) return
    try {
      await api.post(`${BASE}/api-keys/${k.id}/revocar`, {})
      showToast('Clave revocada', 'success')
      cargar()
    } catch { showToast('No se pudo revocar', 'error') }
  }

  return (
    <>
      <div className="pad-block">
        <h2 className="pad-block-title">Claves de API</h2>
        <p className="pad-prose">
          Sirven para generar enlaces de reserva desde tus propios sistemas (por ejemplo, tras un pago).
          La clave completa se muestra <strong>una sola vez</strong>.
        </p>
        <form className="ag-copy" onSubmit={crear}>
          <input className="form-input" placeholder="Nombre de la clave (ej. Web de pagos)"
            value={nombre} onChange={e => setNombre(e.target.value)} />
          <button type="submit" className="btn btn-sm btn-primary" disabled={creando}>
            {creando ? 'Creando…' : 'Crear clave'}
          </button>
        </form>
        {nueva && (
          <div className="pa-code-box">
            <div className="pa-code-label">Tu clave nueva — copiala ahora</div>
            <div className="pa-code">{nueva}</div>
            <p className="pa-code-help">No la volveremos a mostrar. Guardala en un lugar seguro.</p>
            <button className="btn btn-sm btn-secondary" style={{ marginTop: 8 }}
              onClick={() => copiarTexto(nueva).then(ok => showToast(ok ? 'Clave copiada' : 'No se pudo copiar', ok ? 'success' : 'error'))}>
              Copiar
            </button>
          </div>
        )}
      </div>

      <div className="pad-block">
        {loading ? <p className="pad-prose">Cargando claves…</p>
          : keys.length === 0 ? <p className="pad-prose">Todavía no creaste ninguna clave.</p>
            : (
              <div className="pad-tarifas-wrap">
                <table className="pad-tarifas">
                  <thead><tr><th>Nombre</th><th>Prefijo</th><th>Último uso</th><th>Estado</th><th></th></tr></thead>
                  <tbody>
                    {keys.map(k => (
                      <tr key={k.id}>
                        <td><strong>{k.nombre}</strong></td>
                        <td><code>{k.prefijo}…</code></td>
                        <td>{k.ultimoUso ? fmtFechaHora(k.ultimoUso) : 'Nunca'}</td>
                        <td><span className={`badge ${k.revocada ? 'badge-cancelado' : 'badge-activo'}`}>{k.revocada ? 'Revocada' : 'Activa'}</span></td>
                        <td style={{ textAlign: 'right' }}>
                          {!k.revocada && <button className="btn btn-sm btn-ghost" onClick={() => revocar(k)}>Revocar</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </div>
    </>
  )
}

/* ═══════════════ Preguntas de un tipo de reunión ═══════════════ */
export function PreguntasModal({ tipo, onClose }: { tipo: TipoReunion; onClose: () => void }) {
  const api = useApi()
  const [preguntas, setPreguntas] = useState<Pregunta[]>([])
  const [loading, setLoading] = useState(true)
  const [guardando, setGuardando] = useState(false)

  useEffect(() => {
    api.get<Pregunta[]>(`${BASE}/tipos-reunion/${tipo.id}/preguntas`)
      .then(d => setPreguntas(Array.isArray(d) ? d.map(p => ({ ...p, opciones: p.opciones ?? [] })) : []))
      .catch(() => setPreguntas([]))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo.id])

  function set(i: number, patch: Partial<Pregunta>) {
    setPreguntas(ps => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)))
  }

  async function guardar() {
    // Las de opción necesitan al menos una opción (lo valida también Agenda).
    for (const p of preguntas) {
      if ((p.clase === 'opcion_unica' || p.clase === 'opcion_multiple') && p.opciones.length === 0) {
        showToast(`"${p.etiqueta || 'Sin título'}" necesita al menos una opción`, 'error')
        return
      }
    }
    setGuardando(true)
    try {
      await api.put(`${BASE}/tipos-reunion/${tipo.id}/preguntas`, { preguntas })
      showToast('Preguntas guardadas', 'success')
      onClose()
    } catch {
      showToast('No se pudieron guardar las preguntas', 'error')
    } finally { setGuardando(false) }
  }

  return (
    <div className="pa-modal-overlay" onClick={onClose}>
      <div className="pa-modal" onClick={e => e.stopPropagation()}>
        <button type="button" className="pa-modal-close" onClick={onClose} aria-label="Cerrar">✕</button>
        <div className="pa-modal-header"><h3>Preguntas · {tipo.nombre}</h3></div>
        <p className="pad-prose">Además de nombre y email, esto es lo que le pedís al invitado al reservar.</p>

        {loading ? <p className="pad-prose">Cargando…</p> : (
          <>
            {preguntas.length === 0 && <p className="pad-note">Sin preguntas extra.</p>}
            {preguntas.map((p, i) => (
              <div key={i} className="ag-pregunta">
                <div className="ag-form-row">
                  <div className="form-group">
                    <label className="form-label">Pregunta</label>
                    <input className="form-input" value={p.etiqueta} onChange={e => set(i, { etiqueta: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tipo</label>
                    <select className="form-input" value={p.clase} onChange={e => set(i, { clase: e.target.value as ClasePregunta })}>
                      {(Object.keys(CLASE_LABEL) as ClasePregunta[]).map(c => <option key={c} value={c}>{CLASE_LABEL[c]}</option>)}
                    </select>
                  </div>
                </div>
                {(p.clase === 'opcion_unica' || p.clase === 'opcion_multiple') && (
                  <div className="form-group">
                    <label className="form-label">Opciones (una por línea)</label>
                    <textarea className="form-input" rows={3} value={p.opciones.join('\n')}
                      onChange={e => set(i, { opciones: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })} />
                  </div>
                )}
                <div className="ag-pregunta-pie">
                  <label className="ag-check">
                    <input type="checkbox" checked={p.obligatoria} onChange={e => set(i, { obligatoria: e.target.checked })} />
                    Obligatoria
                  </label>
                  <button className="btn btn-sm btn-ghost" onClick={() => setPreguntas(ps => ps.filter((_, j) => j !== i))}>Quitar</button>
                </div>
              </div>
            ))}
            <div className="ag-toolbar" style={{ marginTop: 12 }}>
              <button className="btn btn-sm btn-secondary"
                onClick={() => setPreguntas(ps => [...ps, { etiqueta: '', ayuda: null, clase: 'texto', opciones: [], obligatoria: false }])}>
                + Agregar pregunta
              </button>
              <button className="btn btn-primary" onClick={guardar} disabled={guardando}>
                {guardando ? 'Guardando…' : 'Guardar preguntas'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/* ═══════════════ Shell del módulo ═══════════════ */
export default function AgendaModulo({ tab, publicUrl, orgSlug }: { tab: AgendaTab; publicUrl: string | null; orgSlug: string | null }) {
  return (
    <div className="ag-modulo">
      {(tab === 'tipos' || tab === 'disponibilidad') && <GoogleBanner />}
      {tab === 'tipos' && <TiposReunion publicUrl={publicUrl} orgSlug={orgSlug} />}
      {tab === 'disponibilidad' && <Disponibilidad />}
      {tab === 'reservas' && <Reservas />}
      {tab === 'link' && <MiLink publicUrl={publicUrl} orgSlug={orgSlug} />}
      {tab === 'perfil' && <PerfilAgenda />}
      {tab === 'apikeys' && <ApiKeys />}
    </div>
  )
}
